import React from 'react';
import { motion } from 'framer-motion';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import setup from '../assets/data/setup.json';
import beforeAfterDashboard from '../assets/images/before-after-dashboard.png';
import beforeAfterIntegrations from '../assets/images/before-after-integrations.png';
import beforeAfterDialpad from '../assets/images/before-after-dialpad.png';

const beforeAfterImages = {
  dashboard: beforeAfterDashboard,
  integrations: beforeAfterIntegrations,
  dialpad: beforeAfterDialpad,
} as const;

type BeforeAfterImageKey = keyof typeof beforeAfterImages;

type Profile = typeof setup.profile & {
  heroHeadline?: string;
  heroOneLiner?: string;
};

function Main() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const profile = setup.profile as Profile;
  const data = setup.beforeAfter;
  const activeKey = (data.activeImage in beforeAfterImages
    ? data.activeImage
    : 'dashboard') as BeforeAfterImageKey;
  const beforeAfterImage = beforeAfterImages[activeKey];
  const headline =
    profile.heroHeadline ||
    'I build software that eliminates repetitive work and helps businesses scale.';

  return (
    <section className="relative pt-24 sm:pt-28 pb-12 sm:pb-16">
      <div className="absolute inset-x-0 top-0 h-[520px] pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-muted/35 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-content mx-auto px-5 sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto text-center mb-10 sm:mb-12"
        >
          <p className="ui-eyebrow mb-4 justify-center mx-auto">{profile.name}</p>

          <h1 className="text-[2.15rem] sm:text-5xl lg:text-[3.1rem] font-semibold tracking-[-0.03em] text-ink leading-[1.1] mb-4">
            {headline}
          </h1>

          {profile.heroOneLiner && (
            <p className="text-base sm:text-lg text-ink-secondary leading-relaxed mb-8 max-w-2xl mx-auto">
              {profile.heroOneLiner}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <button onClick={() => scrollTo('case-studies')} className="ui-btn-primary">
              See Case Studies
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            </button>
            <a
              href={profile.calendly}
              target="_blank"
              rel="noreferrer"
              className="ui-btn-secondary"
            >
              <CalendarTodayIcon sx={{ fontSize: 16 }} />
              Book a Discovery Call
            </a>
          </div>
        </motion.div>

        {/* Homepage before/after visual */}
        <motion.div
          id="before-after"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -inset-x-2 -inset-y-2 sm:-inset-x-4 sm:-inset-y-3 rounded-2xl bg-gradient-to-r from-zinc-900/10 via-transparent to-emerald-500/10 blur-xl pointer-events-none" />

          <figure className="relative rounded-xl sm:rounded-2xl overflow-hidden border border-line shadow-card bg-canvas-muted">
            <img
              src={beforeAfterImage}
              alt="Before: overwhelmed by disconnected tools and paperwork. After: one unified dashboard and a business that runs smoothly."
              className="w-full h-auto block"
              loading="eager"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent sm:hidden" />
          </figure>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-5">
            <div className="ui-card p-3.5 sm:p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-red-600 dark:text-red-400 mb-1">
                {data.beforeTitle}
              </p>
              <p className="text-lg sm:text-xl font-semibold text-ink tabular-nums tracking-tight">
                {data.beforeLabel}
              </p>
              <p className="text-[11px] sm:text-xs text-ink-muted mt-0.5">{data.beforeSub}</p>
            </div>
            <div className="ui-card p-3.5 sm:p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-400 mb-1">
                {data.afterTitle}
              </p>
              <p className="text-lg sm:text-xl font-semibold text-ink tabular-nums tracking-tight">
                {data.afterLabel}
              </p>
              <p className="text-[11px] sm:text-xs text-ink-muted mt-0.5">{data.afterSub}</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-8 sm:mt-10">
          <div className="ui-card p-5 sm:p-6">
            <ul className="space-y-3">
              {data.before.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-ink-secondary leading-relaxed">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 flex items-center justify-center">
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="ui-card p-5 sm:p-6">
            <ul className="space-y-3">
              {data.after.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-ink-secondary leading-relaxed">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
                    <CheckIcon sx={{ fontSize: 14 }} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Main;
